import {
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  styled,
  type CardProps,
} from '@mui/material';
import { useState } from 'react';

import { VaultDetailsDialog } from '@/app/(dashboard)/settings/vaults/vaultDetailsDialog';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { CloudProviders } from '@/data/inventory/data';
import type { BackupVault } from '@/data/vaults/backupVault';

import OptionMenu from '../option-menu/option-menu';

interface BackupVaultCardProps {
  data?: BackupVault;
  loading?: boolean;
  style?: object;
  editable: boolean;
  onChange?: () => void;
  onClick?: (vault: BackupVault) => void;
  clickable?: boolean;
  selected?: boolean;
}

interface VaultCardProps extends CardProps {
  clickable: boolean;
  selected?: boolean;
}

const CustomCard = ({ clickable, ...props }: VaultCardProps) => (
  <Card {...props}>{props.children}</Card>
);

const VaultCard = styled(CustomCard)(({ theme, clickable, selected }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  textAlign: 'left',
  minWidth: '300px',
  maxWidth: '420px',
  minHeight: '172px',
  boxShadow: 'none',
  padding: '24px',
  outline: selected
    ? `3px solid ${theme.palette.mode === 'dark' ? '#fff' : '#000'}`
    : clickable
      ? '1px solid transparent'
      : 'none',
  borderRadius: '16px',
  '& .MuiTypography-root': {
    fontSize: '12px',
    color: 'var(--mui-palette-background-black1)',
  },
  '& .MuiCardHeader-root': {
    padding: 0,
    marginBottom: '12px',
  },
  '& .MuiCardContent-root': {
    padding: 0,
  },
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    outline: selected
      ? `3px solid ${theme.palette.mode === 'dark' ? '#fff' : '#000'}`
      : clickable
        ? '1px solid #727283'
        : 'none',
  },
}));

const VaultTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: 'none',
  color: theme.palette.text.secondary,
  padding: '4px 10px 4px 0px',
}));

const VaultTablePropCell = styled(VaultTableCell)(() => {
  return {
    minWidth: '120px',

    '& p': {
      fontWeight: '500',
    },
  };
});

export default function BackupVaultCard(props: BackupVaultCardProps) {
  const { isAuthorized } = useRoles();
  const [isOpen, setIsOpen] = useState(false);
  const dal = useDAL();
  const vaultAccounts = dal.cloudAccounts.vault.list();
  const vaultAccount = vaultAccounts.data?.accounts.find(
    (account) => account.id === props.data?.vaultAccountId
  );

  const id = props.loading
    ? 'loading-vault-card'
    : props.selected
      ? 'backup-vault-card-selected'
      : 'backup-vault-card';

  return (
    <>
      <VaultCard
        selected={props.selected}
        clickable={props.onClick !== undefined}
        onClick={() => props.onClick?.(props.data!)}
        data-testid={id}
        sx={{
          backgroundColor: props?.data?.backgroundColor,
          ...props.style,
        }}
      >
        {props.loading && (
          <>
            <CardHeader component={Skeleton} />
            <CardContent>
              <table className='w-full table-fixed'>
                <tbody>
                  {[...Array(4)].map((_, index) => (
                    <tr key={index}>
                      <td className='px-[5px] pt-[5px] pl-0'>
                        <Skeleton />
                      </td>
                      <td className='pl-[5px] pt-[5px] pr-0'>
                        <Skeleton />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </>
        )}
        {!props.loading && props.data && (
          <>
            <CardHeader
              sx={{ paddingBottom: '5px' }}
              title={props.data.name}
              titleTypographyProps={{
                variant: 'body1',
                sx: {
                  fontSize: '12px',
                  fontWeight: 'bold',
                },
              }}
              action={
                props.editable && (
                  <OptionMenu
                    options={[
                      {
                        id: 'edit-vault',
                        text: 'See vault details',
                        menuItemProps: {
                          disabled: !isAuthorized('update:vaults'),
                          onClick: () => setIsOpen(true),
                        },
                      },
                    ]}
                  />
                )
              }
            />
            <CardContent>
              <TableContainer>
                <Table className='table-fixed'>
                  <tbody>
                    <TableRow>
                      <VaultTablePropCell>
                        <Typography>Cloud</Typography>
                      </VaultTablePropCell>
                      <VaultTableCell>
                        <Typography>
                          {
                            CloudProviders[
                              props.data.vaultAttributes.cloudProvider
                            ]?.displayName
                          }
                        </Typography>
                      </VaultTableCell>
                    </TableRow>
                    <TableRow>
                      <VaultTablePropCell>
                        <Typography>Type</Typography>
                      </VaultTablePropCell>
                      <VaultTableCell>
                        <Typography className='truncate'>
                          {vaultAccount && !vaultAccount.isCustomerOwned
                            ? 'Eon-managed'
                            : 'Self-managed'}
                        </Typography>
                      </VaultTableCell>
                    </TableRow>
                    <TableRow>
                      <VaultTablePropCell>
                        <Typography>Region</Typography>
                      </VaultTablePropCell>
                      <VaultTableCell>
                        <Typography>{props.data.region}</Typography>
                      </VaultTableCell>
                    </TableRow>
                  </tbody>
                </Table>
              </TableContainer>
            </CardContent>
          </>
        )}
      </VaultCard>
      {props.data && isOpen && (
        <VaultDetailsDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          backupVault={props.data}
          vaultAccount={vaultAccount}
          onSaveSuccess={() => {
            props.onChange?.();
          }}
        />
      )}
    </>
  );
}
